import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import type { KeycloakAccountRow } from './types';

interface Props {
    row: KeycloakAccountRow | null;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onConfirm: (endSessions: boolean) => void;
}

/**
 * EAA-1820 (frame 80): lock confirm launched from the profile drawer — simpler
 * than the row-kebab disable (frame 86) because the operator is already looking
 * at the account. No username re-type; the amber "Khoá khác xoá" box carries
 * the distinction from delete.
 */
export function LockAccountDialog({ row, pending, error, onClose, onConfirm }: Props) {
    const { t } = useTranslation();
    const [endSessions, setEndSessions] = useState(false);
    const open = Boolean(row);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={t('console.accounts.lockModal.titleForUser', { username: row?.username })}
            footer={
                <>
                    <span className="acc-delete-footer-note">{t('console.accounts.lockModal.auditFooter')}</span>
                    <button type="button" className="portal-btn" onClick={onClose} disabled={pending}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        onClick={() => onConfirm(endSessions)}
                        disabled={pending}
                    >
                        {pending ? t('console.accounts.lockModal.pending') : t('console.accounts.lockModal.submit')}
                    </button>
                </>
            }
        >
            <p className="portal-admin-hint">{t('console.accounts.lockModal.hint', { username: row?.username })}</p>

            <div className="acc-lock-warn-box" role="note">
                <strong>{t('console.accounts.lockModal.diffHeading')}</strong>
                <p>{t('console.accounts.lockModal.diffBody')}</p>
            </div>

            <label className="acc-lock-sessions-opt">
                <input
                    type="checkbox"
                    checked={endSessions}
                    onChange={(event) => setEndSessions(event.target.checked)}
                />
                <span>
                    {t('console.accounts.lockModal.sessionsLabel')}
                    <span className="acc-lock-sessions-sub">{t('console.accounts.lockModal.sessionsSub')}</span>
                </span>
            </label>

            {error && <p className="portal-admin-error">{error}</p>}
        </Modal>
    );
}

export default LockAccountDialog;
